import React, { useRef, useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  ListItemIcon,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import { useIssues } from '../context/IssueContext';
import { useArticles } from '../context/ArticleContext';
import { Article } from '../types/article';
import { Issue } from '../types/issue';
import { useApp } from '../context/AppContext';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import api from '../services/api';

// Стилизованные компоненты
const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  backgroundImage: 'url(/images/banner1.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'right',
  color: 'white',
  padding: theme.spacing(8, 0),
  marginBottom: theme.spacing(6),
  minHeight: 400,
  display: 'flex',
  alignItems: 'center',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(0, 56, 168, 0.7)', // синий overlay
    zIndex: 1,
    borderRadius: theme.shape.borderRadius,
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  position: 'relative',
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: 0,
    width: 60,
    height: 3,
    backgroundColor: theme.palette.primary.main,
  },
})) as typeof Typography;

interface News {
  id: string;
  title: string;
  content: string;
  imageUrl: string;
  createdAt: string;
}

const Home: React.FC = () => {
  const [news, setNews] = useState<News[]>([]);
  const { issues, loading: issuesLoading, error: issuesError, fetchIssues } = useIssues();
  const { articles, loading: articlesLoading, error: articlesError, fetchArticles } = useArticles();
  const { isInitialized } = useApp();

  useEffect(() => {
    if (!isInitialized) {
      fetchIssues();
      fetchArticles();
      fetchNews();
    }
  }, [fetchIssues, fetchArticles, isInitialized]);

  const fetchNews = async () => {
    try {
      const response = await api.get<News[]>('/news/published');
      setNews(response.data);
    } catch (error) {
      console.error('Ошибка при загрузке новостей:', error);
    }
  };

  // Получаем последние 3 выпуска
  const latestIssues = React.useMemo(() => {
    return issues
      .filter(issue => issue.status === 'published')
      .sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.number - a.number;
      })
      .slice(0, 3);
  }, [issues]);

  // Получаем 3 самые популярные статьи
  const popularArticles = React.useMemo(() => {
    return articles
      .filter(article => article.status === 'published')
      .sort((a, b) => b.views - a.views)
      .slice(0, 3);
  }, [articles]);

  if (issuesError || articlesError) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>
          {issuesError || articlesError}
        </Alert>
      </Container>
    );
  }

  if (issuesLoading || articlesLoading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Тюменский научный журнал | Главная</title>
        <meta name="description" content="Тюменский научный журнал - рецензируемое научное издание, публикующее оригинальные исследования в различных областях науки. ISSN: 3034-154Х" />
        <meta name="keywords" content="научный журнал, тюмень, публикации, статьи, научные исследования, ISSN 3034-154Х" />
        <meta property="og:title" content="Тюменский научный журнал" />
        <meta property="og:description" content="Рецензируемое научное издание, публикующее оригинальные исследования в различных областях науки." />
        <meta property="og:image" content="https://tyumen-science.ru/logo.png" />
        <meta property="og:url" content="https://tyumen-science.ru" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Тюменский научный журнал" />
        <meta name="twitter:description" content="Рецензируемое научное издание, публикующее оригинальные исследования в различных областях науки." />
        <meta name="twitter:image" content="https://tyumen-science.ru/logo.png" />
        <link rel="canonical" href="https://tyumen-science.ru" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Periodical",
              "name": "Тюменский научный журнал",
              "description": "Рецензируемое научное издание, публикующее оригинальные исследования в различных областях науки",
              "url": "https://tyumen-science.ru",
              "publisher": {
                "@type": "Organization",
                "name": "Тюменский научный журнал"
              },
              "issn": "3034-154Х"
            })
          }}
        />
      </Helmet>
      <HeroSection>
        <Container sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ position: 'relative', zIndex: 2, maxWidth: { xs: '100%', md: '60%' } }}>
            <Typography variant="h2" component="h1" gutterBottom>
              "Посев научный взойдёт для жатвы народной"
            </Typography>
            <Typography variant="h5" sx={{ mb: 2, fontStyle: 'italic' }}>
              Д.И. Менделеев
            </Typography>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Публикация научных исследований и статей
            </Typography>
            <Button
              component={Link}
              to="/articles"
              variant="contained"
              color="secondary"
              size="large"
            >
              Читать статьи
            </Button>
          </Box>
        </Container>
      </HeroSection>

      <Container>
        <Grid container spacing={4}>
          {/* Последние выпуски */}
          <Grid item xs={12} md={8}>
            <SectionTitle variant="h4" component="h2">
              Последние выпуски
            </SectionTitle>
            <Grid container spacing={3}>
              {latestIssues.map((issue) => (
                <Grid item xs={12} sm={4} key={issue.id}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {issue.title}
                      </Typography>
                      <Typography variant="body2">
                        Статей: {issue.articles?.length || 0}
                      </Typography>
                      <Button
                        component={Link}
                        to={`/issues/${issue.id}/${issue.title.toLowerCase().replace(/\s+/g, '-')}`}
                        variant="outlined"
                        size="small"
                        sx={{ mt: 2 }}
                      >
                        Подробнее
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Новости и объявления */}
          <Grid item xs={12} md={4}>
            <SectionTitle variant="h4" component="h2">
              Новости
            </SectionTitle>
            <Paper>
              <List>
                {news.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <ListItem>
                      <ListItemText
                        primary={item.title}
                        secondary={new Date(item.createdAt).toLocaleDateString()}
                      />
                    </ListItem>
                    {index < news.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
                {news.length === 0 && (
                  <ListItem>
                    <ListItemText
                      primary="Новостей пока нет"
                      secondary="Скоро появятся новые публикации"
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>

          {/* Популярные статьи */}
          <Grid item xs={12}>
            <SectionTitle variant="h4" component="h2">
              Популярные статьи
            </SectionTitle>
            <Grid container spacing={3}>
              {popularArticles.map((article) => (
                <Grid item xs={12} sm={4} key={article.id}>
                  <Card>
                    <CardContent>
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          lineHeight: '1.4',
                          height: '5.6em' // 4 строки * 1.4 line-height
                        }}
                      >
                        {article.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        paragraph
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          height: '6em', // 4 строки * 1.5 line-height
                          lineHeight: '1.5em'
                        }}
                      >
                        {article.abstract}
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Chip
                          label={`${article.views} просмотров`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                      <Button
                        component={Link}
                        to={`/articles/${article.id}/${article.title.toLowerCase().replace(/\s+/g, '-')}`}
                        variant="text"
                        size="small"
                        sx={{ mt: 2 }}
                      >
                        Читать статью
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* О журнале */}
          <Grid item xs={12}>
            <SectionTitle variant="h4" component="h2">
              О журнале
            </SectionTitle>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" paragraph>
                  Тюменский научный журнал - это рецензируемое научное издание,
                  публикующее оригинальные исследования в различных областях науки.
                  Наш журнал призван способствовать развитию научной мысли и обмену
                  знаниями между исследователями.
                </Typography>
                <Typography variant="body1" paragraph>
                  Мы публикуем статьи по следующим направлениям:
                </Typography>
                <Grid container spacing={1}>
                  {[
                    'Нефтегазовое дело',
                    'Безопасность',
                    'Интеллектуальные технологии',
                    'Машиностроение',
                    'ТЭК',
                    'Экология',
                    'Экономика ТЭК',
                  ].map((category) => (
                    <Grid item key={category}>
                      <Chip label={category} color="primary" variant="outlined" />
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Статистика журнала
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="h4" color="primary">
                          {articles.length}+
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Опубликованных статей
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="h4" color="primary">
                          {new Set(articles.flatMap(article => article.authors?.map(author => author.id) || [])).size}+
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Авторов
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="h4" color="primary">
                          {issues.length}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Выпусков
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Полезные ресурсы */}
          <Grid item xs={12}>
            <Grid container spacing={3} justifyContent="center">
              <Grid item xs={12} sm={6} md={4}>
                <Card 
                  component="a" 
                  href="https://cyberleninka.ru/journal/n/tyumenskiy-nauchnyy-zhurnal?i=1146679" 
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    textDecoration: 'none',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.02)'
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Box
                      component="img"
                      src="/images/cyberleninka-logo.png"
                      alt="CyberLeninka"
                      sx={{ 
                        height: 70,
                        mb: 2
                      }}
                    />
                    <Typography variant="body1" color="text.primary">
                      CyberLeninka
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Научная электронная библиотека
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card 
                  component="a" 
                  href="https://elibrary.ru/title_about_new.asp?id=148258" 
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    textDecoration: 'none',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.02)'
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Box
                      component="img"
                      src="/images/elibrary-logo.png"
                      alt="eLibrary"
                      sx={{ 
                        height: 70,
                        mb: 2
                      }}
                    />
                    <Typography variant="body1" color="text.primary">
                      eLibrary
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Российский индекс научного цитирования
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default Home; 